/**
 * useBuildVersionCheck + a module-level version-state store consumed by
 * the existing dashboards (DevStatusBar build chip, DiagnosticHub
 * RuntimeHealthTab "Build Version" card).
 *
 * The hook is the slow-poll producer (5 min) — keeps drift detection
 * alive when no dashboard is open. The DiagnosticHub API sweep is the
 * fast-poll producer (7 s when its tab is open) — it routes its
 * /api/version response into the same store via `recordServerSha()`.
 *
 * Anyone who wants to render version state calls `getVersionState()`.
 * No subscription needed — DevStatusBar's existing 2 s interval and
 * RuntimeHealthTab's parent-driven re-render both naturally pick up
 * fresh state on their next tick.
 *
 * The toast (one-time "New build available") is preserved.
 *
 * Server side: src/server/routes/version.routes.ts reads `.source_sha`
 * (written by scripts/vps/deploy.sh on every deploy).
 *
 * Pair with the unified ?reset=1 wipe (src/main.tsx) so DJ has two
 * orthogonal handles:
 *   - "I think my browser cache is stale"          → ?reset=1 / /reset
 *   - "There IS actually a newer bundle deployed"  → this toast / chip
 */

import { useEffect } from 'react';
import { gameLogger } from '../../utils/GameLogger';

const POLL_MS = 5 * 60 * 1000; // 5 minutes — slow background poll
const FETCH_TIMEOUT_MS = 5_000;

export interface VersionState {
  /** SHA observed on first /api/version response after page boot. Stable. */
  bootSha: string | null;
  /** Most recent SHA seen — same as bootSha until a deploy lands while open. */
  lastServerSha: string | null;
  /** Server's reported env field (development | production). */
  env: string | null;
  /** Date.now() of last successful /api/version response. */
  lastCheckedAt: number | null;
  /** True iff bootSha is set and lastServerSha differs. */
  drift: boolean;
  /** True iff at least one /api/version call has completed (any source). */
  hasData: boolean;
}

let _bootSha: string | null = null;
let _lastServerSha: string | null = null;
let _env: string | null = null;
let _lastCheckedAt: number | null = null;
let _toasted = false;

/** Display form of a build sha — 40-hex git shas abbreviate to 8 chars;
 *  dev fallback ids (`dev-<pid>-<ts>`) show verbatim so a non-deploy build
 *  is unmistakable. Shared by the picker chip + the in-game map pill. */
export const shortSha = (sha: string): string => (/^[0-9a-f]{40}$/i.test(sha) ? sha.slice(0, 8) : sha);

export function getVersionState(): VersionState {
  const drift = !!_bootSha && !!_lastServerSha && _bootSha !== _lastServerSha;
  return {
    bootSha: _bootSha,
    lastServerSha: _lastServerSha,
    env: _env,
    lastCheckedAt: _lastCheckedAt,
    drift,
    hasData: _lastServerSha !== null,
  };
}

/**
 * Record a fresh server SHA observation. Called by both the slow-poll
 * hook and DiagnosticHub's API sweep. First call sets bootSha; later
 * calls update lastServerSha. Side-effect: fires the one-time drift
 * toast on the first observed mismatch.
 */
export function recordServerSha(sha: string, env?: string): void {
  if (!sha) return;
  _lastServerSha = sha;
  _lastCheckedAt = Date.now();
  if (env) _env = env;
  if (_bootSha === null) {
    _bootSha = sha;
    gameLogger.info('SYSTEM', `[BuildVersionCheck] boot sha=${sha} env=${env ?? 'unknown'}`);
    return;
  }
  if (_bootSha !== sha && !_toasted) {
    _toasted = true;
    gameLogger.warn('SYSTEM', `[BuildVersionCheck] drift detected: boot=${_bootSha} → server=${sha}`);
    (globalThis as any).__errorToast?.(
      'warn',
      'New build available — refresh to update',
    );
  }
}

export interface VersionResponse { sha: string; env: string }

/** One-shot /api/version fetch (5s abort, never throws). Exported for
 *  surfaces that render version state outside the game boot path — e.g.
 *  the HomeModePicker build chip fetches once on mount and feeds the
 *  shared store via recordServerSha. */
export async function fetchVersion(): Promise<VersionResponse | null> {
  try {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);
    const r = await fetch('/api/version', {
      method: 'GET',
      credentials: 'same-origin',
      signal: ac.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);
    if (!r.ok) return null;
    const data = (await r.json()) as VersionResponse;
    if (!data?.sha) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Background poll loop — runs once per app boot from Game.tsx. Idempotent
 * across React strict-mode double-mount: guarded by a module flag.
 */
let _hookMounted = false;
export function useBuildVersionCheck(): void {
  useEffect(() => {
    if (_hookMounted) return;
    _hookMounted = true;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    (async () => {
      const initial = await fetchVersion();
      if (cancelled || !initial) {
        if (!initial) {
          gameLogger.debug('SYSTEM', '[BuildVersionCheck] initial /api/version unreachable; will retry on tick');
        }
        // schedule poll regardless so the chip eventually populates
        intervalId = setInterval(async () => {
          if (cancelled) return;
          const latest = await fetchVersion();
          if (latest) recordServerSha(latest.sha, latest.env);
        }, POLL_MS);
        return;
      }
      recordServerSha(initial.sha, initial.env);

      intervalId = setInterval(async () => {
        if (cancelled) return;
        const latest = await fetchVersion();
        if (latest) recordServerSha(latest.sha, latest.env);
      }, POLL_MS);
    })();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      _hookMounted = false;
    };
  }, []);
}
